import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { IUnreadPushNotification, PushProvider } from './push.provider';
import { environment } from 'src/environments/environment';
import { PermissionEnum } from 'src/constants/permissions';
import { CommonPushNotification, NotificationAction, NotificationOpenPage, NotificationType } from 'src/shared/models/push-notification/push-notification';
import { PermissionService } from '../permission.service';
import { NewsProvider } from '../news.provider';
import { AuthenticatedHttpService, OfflineWrapResult } from '../authenticatedHttpService.service';
import { RequestListPage } from 'src/app/pages/requests/requests.page';
import { Page, UserMode } from '../tabs/tabs-meta';
import { EnquiriesPage } from 'src/app/pages/enquiries/enquiries.page';
import { StatisticsPage } from 'src/app/pages/statistics/statistics.page';
import { PresenceMonitoringPage } from 'src/app/pages/presence-monitoring/presence-monitoring.page';
import { TabsProvider } from '../tabs/tabs.provider';
import { Badge } from '@capawesome/capacitor-badge';
import { ModalProvider } from '../modal.provider';

@Injectable({ providedIn: 'root' })
export class PushManagerProvider {
    private readonly _unreadNotifications = signal<IUnreadPushNotification[]>([]);
    public readonly unreadNotifications = this._unreadNotifications.asReadonly();

    private readonly _initUnreadCountRequired = signal<boolean | null>(true);
    public readonly initUnreadCountRequired = this._initUnreadCountRequired.asReadonly();

    private readonly _notificationToProceed = signal<CommonPushNotification | null>(null);
    public readonly notificationToProceed = this._notificationToProceed.asReadonly();
    public notificationToProceed$ = toObservable(this._notificationToProceed);

    constructor(
        private pushProvider: PushProvider,
        private permissionService: PermissionService,
        private newsProvider: NewsProvider,
        private modalProdiver: ModalProvider,
        private tabsProvider: TabsProvider,
        private http: AuthenticatedHttpService,
    ) { }

    init() {
        this.pushProvider.onPushNotification$
            .subscribe(notification => this.notificationManager(notification))
    }

    async fetchUnreadCountByNotificationType(): Promise<boolean> {
        return new Promise((async (resolve) => {
            try {
                this._initUnreadCountRequired.set(null);

                const response = await this.http.wrapIntoOffline<IUnreadPushNotification[] | null>({
                    fetch: this.http.get,
                    params: [`${environment.web.apiUrl}me/notifications/unread-count-by-type`],
                    storeKey: PushProvider.PUSH_NOTIFICATIONS,
                    renew: false
                });

                if (response.status === OfflineWrapResult.Success) {
                    this._unreadNotifications.set(response.result!);

                    const totalUnreadCount = this._unreadNotifications().reduce((sum, notification) => sum + notification.count, 0);
                    await Badge.set({ count: totalUnreadCount });
                }
                else {
                    this._initUnreadCountRequired.set(true);
                    this._unreadNotifications.set([]);
                }

            } catch (error) {
                console.error(error);
            }
            finally {
                resolve(true);
            }
        }));
    }

    async fetchNotifications() {
        if (this.permissionService.hasPermission(PermissionEnum.News)) {
            await Promise.all([
                this.fetchUnreadCountByNotificationType(),
                this.newsProvider.fetchUnreadCount(),
            ]);
        }
        else {
            await this.fetchUnreadCountByNotificationType();
        }
    }

    isRead(type: NotificationType) {
        return this._unreadNotifications().findIndex(n => n.type === type) === -1;
    }

    async continueProcessingClickedNotification() {
        if (!this._notificationToProceed()) return;
        await this.openPage(this._notificationToProceed()!);
    }

    clearNotificationToProceed() {
        this._notificationToProceed.set(null);
    }

    private async notificationManager(notification: CommonPushNotification) {
        if (notification.clickNotification) {
            if (notification.clickAction == NotificationAction.OpenPage) {
                this.openPage(notification);
            }
        }
        else {
            await this.fetchNotifications();
        }
    }

    private async openPage(notification: CommonPushNotification) {
        const page = notification.associatedPage;
        const mode = this.http.getBookingMode();

        if (mode === UserMode.Unknown) {
            this._notificationToProceed.set(notification);
            return;
        }

        const openModal = async (component: any, id?: string) => {
            await this.modalProdiver.openModal({
                component: component,
                id: id,
            })
        };

        const openPage = async (page: Page) => {
            const isVisible = this.tabsProvider.availableTabs.find(t => t.tab === page)!.visible;
            if (isVisible) {
                this.tabsProvider.activateTab(page);
            }
        };

        switch (page) {
            case NotificationOpenPage.RequestsPage:
                await openModal(RequestListPage);
                break;

            case NotificationOpenPage.EnquiriesPage:
                await openModal(EnquiriesPage);
                break;

            case NotificationOpenPage.StatisticsPage:
                await openModal(StatisticsPage);
                break;

            case NotificationOpenPage.PinboardPage:
                const page = mode === UserMode.SingleUser ? Page.SingleuserPinboard : Page.MultiuserPinboard;
                openPage(page);
                break;

            case NotificationOpenPage.PresenceMonitoringPage:
                await openModal(PresenceMonitoringPage, 'monitoring');
                break;

            case NotificationOpenPage.MessagesPage:
                this._notificationToProceed.set(notification);
                openPage(Page.SingleuserNews);
                break;
        }
    }
}